import {useEffect, useMemo, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {WebGLDither, type DitherParams} from '@github-ui/brand-webgl'

// Debounce window for coalescing container resizes (e.g. while images load)
// before remounting the dither.
const RESIZE_SETTLE_MS = 100

interface UseDitherBackgroundOptions {
  className?: string
  enabled?: boolean
  params: DitherParams
}

/**
 * Portals a `WebGLDither` background into a container captured via callback ref.
 * Needed because Primer Brand's `Hero` filters out arbitrary children:
 * https://github.com/primer/brand/issues/1356
 *
 * `enabled` lives in the hook so callers can opt out without splitting their
 * render path. That keeps the effect's setup and cleanup logic in one place,
 * and lets React rerun or tear down the observer when the opt-in state changes.
 */
export function useDitherBackground({className, enabled = true, params}: UseDitherBackgroundOptions): {
  containerRef: {current: HTMLDivElement | null}
  ditherBackground: ReactNode
} {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const [sizeKey, setSizeKey] = useState<string | null>(null)

  const containerRef = useMemo(() => {
    let current: HTMLDivElement | null = null
    return {
      get current() {
        return current
      },
      set current(value: HTMLDivElement | null) {
        if (current !== value) {
          current = value
          setContainerEl(value)
        }
      },
    }
  }, [])

  useEffect(() => {
    if (!enabled || !containerEl) return

    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const scheduleUpdate = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const {width, height} = containerEl.getBoundingClientRect()
        if (width === 0 || height === 0) return
        const next = `${Math.round(width)}x${Math.round(height)}`
        setSizeKey(prev => (prev === next ? prev : next))
      }, RESIZE_SETTLE_MS)
    }

    const observer = new ResizeObserver(scheduleUpdate)
    observer.observe(containerEl)
    scheduleUpdate()

    return () => {
      observer.disconnect()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [containerEl, enabled])

  const ditherBackground: ReactNode =
    enabled && containerEl && sizeKey
      ? createPortal(
          <WebGLDither key={sizeKey} className={className} params={params} offsetByWrapperTop />,
          containerEl,
        )
      : null

  return {containerRef, ditherBackground}
}
